import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useCallbackAuthorizeUrlMutation,
	useGetExternalIntegrationQuery,
} from '../../../../queries';
import { useDisconnectUserFromIntegrationMutation } from '../../../../queries/Integrations/useDisconnectUserFromIntegrationMutation';
import casePeerLogo from '../../../assets/logo_8am_casepeer.png';
import { Button } from '../../../components-v2/Button';
import { DisconnectSyncUserModal } from '../../../components/integrations/DisconnectSyncUserModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../components-v2/Tabs';
import { InProgressIcon } from '../../../components/svgs/icons/InProgressIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { background, nameCircles } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Connect = 'connect',
	Manage = 'manage',
}

enum IntegrationState {
	NotConnected = 'not-connected',
	Connected = 'connected',
}

function _CasePeer() {
	const history = useHistory();
	const userSession = useUserSession();
	const params = useParams<{ tab: TabValues }>();
	const [isDisableModalOpen, setIsDisableModalOpen] = React.useState(false);

	const disableIntegrationMutation = useDisconnectUserFromIntegrationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			toast.success('Integration disabled successfully');
			invalidateIntegrationQuery({
				provider: Api.IntegrationProvider.CasePeer,
			});
			history.push('/integrations');
		},
	});
	const callbackAuthorizeUrlMutation = useCallbackAuthorizeUrlMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: url => {
			window.location.replace(url);
		},
	});

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.CasePeer,
	});

	const changeTab = (value: string) => {
		history.replace(`/integrations/${Api.IntegrationProvider.CasePeer}/${value}`);
	};

	const integrationState = React.useMemo(() => {
		const userConnections = integrationQuery.data?.configuration?.userConnections || [];
		const userConnection = userConnections.find(connection => connection.userId === userSession.user.id);

		return userConnection?.isConnected ? IntegrationState.Connected : IntegrationState.NotConnected;
	}, [integrationQuery.data, userSession.user.id]);

	React.useEffect(() => {
		if (!integrationQuery.data) {
			if (!params.tab) {
				changeTab(TabValues.Connect);
			}
			return;
		}

		if (integrationState === IntegrationState.Connected) {
			changeTab(TabValues.Manage);
		} else {
			changeTab(TabValues.Connect);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationQuery.data, integrationState]);

	const connect = () => {
		callbackAuthorizeUrlMutation.mutate({
			provider: Api.IntegrationProvider.CasePeer,
		});
	};

	const onDisable = () => {
		disableIntegrationMutation.mutate(Api.ExternalIntegrationType.CasePeer);
	};

	if (integrationQuery.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const isConnected = integrationState === IntegrationState.Connected;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(integrationsStyleSheet.logo, integrationsStyleSheet.headerLogo, styleSheet.headerLogo)}>
				<img src={casePeerLogo} alt='CasePeer Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with CasePeer, and pull client information, including names, email, phone number, birthdays,
				and more.
			</p>
			<TabRoot className={`tab-view ${css(styleSheet.tabView)}`} value={params.tab} onValueChange={changeTab}>
				<TabList styles={[styleSheet.tabViewTabs]}>
					<TabTrigger
						value={TabValues.Connect}
						styles={[styleSheet.tabViewItem, params.tab === TabValues.Connect && styleSheet.tabItemSelected]}
					>
						<SuccessCheckIcon fillColor={isConnected ? undefined : nameCircles} ringColor={background} />
						<span>Connect</span>
					</TabTrigger>

					<TabTrigger
						value={TabValues.Manage}
						styles={[styleSheet.tabViewItem, params.tab === TabValues.Manage && styleSheet.tabItemSelected]}
					>
						<span>Manage</span>
					</TabTrigger>
				</TabList>
				<TabContent value={TabValues.Connect} className='tab-view-content'>
					{isConnected ? (
						<div className={css(styleSheet.section)}>
							<p className={css(integrationsStyleSheet.bodyText)}>
								If you wish to disable this integration for your user, click Disconnect.
							</p>
							<Button
								variant='primary'
								onClick={ev => {
									ev.preventDefault();
									if (integrationQuery.data?.configuration?.syncUserId === userSession.user.id) {
										setIsDisableModalOpen(true);
									} else {
										onDisable();
									}
								}}
							>
								Disconnect
							</Button>
						</div>
					) : (
						<div className={css(styleSheet.section)}>
							<div className={css(styleSheet.inProgressIconContainer)}>
								<InProgressIcon />
							</div>
							<p className={css(integrationsStyleSheet.bodyText)}>
								To connect your user to the account, click Connect.
							</p>
							<div>
								<Button variant='primary' onClick={() => connect()}>
									Connect
								</Button>
							</div>
						</div>
					)}
				</TabContent>

				<TabContent value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							Once you are connected, your CasePeer data will show up in the contacts tab.
						</p>
						<Link to='/people' className={css(bs.ctaButton)}>
							Contacts
						</Link>
					</div>
				</TabContent>
			</TabRoot>

			<DisconnectSyncUserModal
				integrationDisplayName='CasePeer'
				isDisableModalOpen={isDisableModalOpen}
				onDisable={onDisable}
				setIsDisableModalOpen={setIsDisableModalOpen}
			/>
		</div>
	);
}

const CasePeer = observer(_CasePeer);
export { CasePeer };
